import React, { useMemo, useState } from 'react';
import { ImageBackground } from 'react-native';

import { Box, Inline } from '@styleseat/ui';
import { colors } from '@styleseat/brand';
import { useResponsiveEffect, useAndAssignTest, useIntersectionTrigger } from '../../../../../hooks';

import {
  Stack,
  BodyText,
  Touchable,
  HeaderText,
} from '../../../../ui';

import analytics from '../../../../../modules/analytics';
import useGoToSearch from '../../../../consumer/booking/BookingFlow/hooks/useGoToSearch';

import { MediumSearchTile } from '../../../searchTiles';
import { SearchPopoverModal } from '../../../../consumer/search/SearchPopover';

import ProviderProfileMarketplaceHeroTwo from '../../../../../../images/providerProfile/marketplace/provider-profile-marketplace-hero-2.png';

import { ProviderRecommendationData } from '../../../../../store/Recommendations.model';

import {
  MARKETPLACE_BASE_NAME,
  COM_793_STYLE_PERCENTAGES,
  SHOW_AVAILABILITY_BADGES_COM_793_TEST_NAME,
  SHOW_AVAILABILITY_BADGES_COM_793_TEST_PERCENT,
} from '../constants';

const getTestID = (elementName: string) => `${MARKETPLACE_BASE_NAME}__${elementName}`;

export type ServiceTilesMobileProps = {
  providerRecommendation: ProviderRecommendationData;
};

const Com793ShowMediumSearchTile = ({
  suggestion, index, isInCom793Test, providerRecommendation,
}) => {
  const goToSearch = useGoToSearch();
  const onMarketplaceCategoryClick = React.useCallback((searchTerm: string) => {
    goToSearch(`${MARKETPLACE_BASE_NAME}_tile`, {
      searchTerm,
      city: providerRecommendation.city,
      state: providerRecommendation.state,
    });
  }, [goToSearch, providerRecommendation]);

  const createRandomPercentages = (styles = {}) => {
    const randomPercents = {};
    const keys = Object.keys(styles);
    keys.forEach(key => {
      const min = styles[key].min_percent_booked;
      const max = styles[key].max_percent_booked;
      const randomPercentage = Math.floor(Math.random() * (max - min + 1) + min);
      randomPercents[key] = randomPercentage;
    });
    return randomPercents;
  };

  const styleRandomPercents = useMemo(() => createRandomPercentages(COM_793_STYLE_PERCENTAGES), []);

  if (isInCom793Test && styleRandomPercents[suggestion.search_term]) {
    return (
      <MediumSearchTile
        key={suggestion.search_term}
        baseTrackingName={MARKETPLACE_BASE_NAME}
        displayPosition={index + 1}
        imageSource={suggestion.image_url}
        categoryName={suggestion.search_term}
        testID={getTestID(`tile-${index + 1}`)}
        onClick={() => onMarketplaceCategoryClick(suggestion.search_term)}
        hasBadge
        badgeText={`${styleRandomPercents[suggestion.search_term].toString()}% full!`}
      />
    );
  }
  return (
    <MediumSearchTile
      key={suggestion.search_term}
      baseTrackingName={MARKETPLACE_BASE_NAME}
      displayPosition={index + 1}
      imageSource={suggestion.image_url}
      categoryName={suggestion.search_term}
      testID={getTestID(`tile-${index + 1}`)}
      onClick={() => onMarketplaceCategoryClick(suggestion.search_term)}
      hasBadge={false}
    />
  );
};

export default function ServiceTilesMobile({ providerRecommendation }: ServiceTilesMobileProps) {
  const is = useResponsiveEffect();

  const isInCom793Test = useAndAssignTest(
    SHOW_AVAILABILITY_BADGES_COM_793_TEST_NAME,
    SHOW_AVAILABILITY_BADGES_COM_793_TEST_PERCENT,
  );

  let displayMarketplaceCategoryName = providerRecommendation.profession.toLowerCase();
  if (displayMarketplaceCategoryName.slice(-1) !== 's') {
    displayMarketplaceCategoryName += 's';
  }

  const shownSuggestions = React.useMemo(() => {
    const trimmedSuggestions = providerRecommendation.top_categories?.slice(0, 6) || [];
    // If we have fewer than a multiple of 3 then we have to pad out
    // the list to a multiple of three.
    const numPad = 3 - (trimmedSuggestions.length % 3);
    if (numPad < 3) {
      return [
        ...trimmedSuggestions,
        ...Array(numPad).fill(null),
      ];
    }

    return trimmedSuggestions;
  }, [providerRecommendation.top_categories]);

  const [showSearch, setShowSearch] = React.useState<boolean>(false);

  const handleShowSearch = (trackingSource: string) => () => {
    analytics.track(trackingSource);
    setShowSearch(true);
  };

  const handleCloseSearch = () => {
    setShowSearch(false);
  };

  const [trackingOfMediumSearchTiles, setTrackingOfMediumSearchTiles] = useState(true);

  const tilesReached = () => {
    if (isInCom793Test) {
      analytics.track(`${MARKETPLACE_BASE_NAME}_percentage_badges_are_in_viewport`);
      setTrackingOfMediumSearchTiles(false);
    }
  };

  const bottomTilesRef = useIntersectionTrigger(tilesReached, trackingOfMediumSearchTiles);

  return (
    <Box alignItems="center" testID="provider-profile-marketplace">
      <Stack
        maxWidth={415}
        testID="provider-profile-marketplace-updated-ui"
      >
        <Box>
          <ImageBackground
            source={ProviderProfileMarketplaceHeroTwo}
            style={{ paddingBottom: 24 }}
          >
            <Stack
              space={4}
              paddingX={16}
              paddingTop={80}
              paddingBottom={16}
            >
              <HeaderText
                color={colors.whiteWash}
                fontSize={40}
                lineHeight={44}
                testID={getTestID('header')}
              >
                {`Search for \nmore ${displayMarketplaceCategoryName}`}
              </HeaderText>
            </Stack>
          </ImageBackground>
        </Box>
        <Stack
          borderRadiusTop={24}
          paddingTop={24}
          style={{ backgroundColor: colors.whiteWash, marginTop: -24 }}
        >
          <Inline paddingX={16}>
            <HeaderText fontSize={16} lineHeight={20}>
              {`${providerRecommendation.nearby_pros} plus ${displayMarketplaceCategoryName} in your area`}
            </HeaderText>
          </Inline>
          <Inline
            paddingTop={is.mobileNarrow ? 12 : 16}
            paddingX={12}
            flexWrap="wrap"
            justifyContent="space-between"
          >
            {shownSuggestions.map((suggestion, index) => (
              suggestion !== null ? (
                <Com793ShowMediumSearchTile
                  key={suggestion?.search_term}
                  suggestion={suggestion}
                  index={index}
                  isInCom793Test={isInCom793Test}
                  providerRecommendation={providerRecommendation}
                />
              ) : (
                <Box
                  // eslint-disable-next-line react/no-array-index-key
                  key={`filler-${index}`}
                  flex={1}
                  flexGrow={1}
                  padding={4}
                  flexBasis={0}
                  minWidth="calc(100%/3)"
                />
              )
            ))}
          </Inline>
          <span
            ref={bottomTilesRef}
          />
        </Stack>
        <Box
          paddingBottom={36}
          alignItems="center"
        >
          <Touchable
            onClick={handleShowSearch(`${MARKETPLACE_BASE_NAME}_discover_more_services_clicked`)}
            testID={getTestID('discover-more-button')}
          >
            <Box
              borderStyle="solid"
              borderBottomWidth={1}
              borderBottomColor={colors.blackInBlack}
              testID={getTestID('discover-more-bottom')}
            >
              <BodyText>
                More Services
              </BodyText>
            </Box>
          </Touchable>
        </Box>
        {showSearch && (
          <SearchPopoverModal
            onClose={handleCloseSearch}
            source={`${MARKETPLACE_BASE_NAME}_search_popover`}
          />
        )}
      </Stack>
    </Box>
  );
}
